import React, { useEffect, useState } from "react";
import { SectionCard } from "./common";
import { TField, TSelect } from "./forms";
import { GoldButton, OutlineButton, EmptyState, ui } from "@/components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Plus, CalendarDays, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function CalendarTab({ property, options }) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", type: "Rates Due", date: new Date().toISOString().slice(0, 10), notes: "" });
  const load = () => api.get(`/properties/${property.id}/events`).then((r) => setEvents(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [property.id]);

  const add = async () => {
    if (!form.title) return toast.error("Add a title");
    setSaving(true);
    try {
      await api.post(`/properties/${property.id}/events`, form);
      toast.success("Reminder added"); setOpen(false);
      setForm({ ...form, title: "", notes: "" }); load();
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/properties/${property.id}/events/${id}`); load(); };

  const today = new Date().toISOString().slice(0, 10);
  return (
    <SectionCard title="Calendar" action={
      <GoldButton data-testid="cal-add" onClick={() => setOpen(true)}><Plus size={15} /> Add reminder</GoldButton>
    }>
      {events.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No important dates yet" hint="Track rates, mortgage reviews, lease expiry, insurance renewal, smoke alarm & pool inspections, maintenance and tax dates." />
      ) : (
        <div className="space-y-2" data-testid="cal-list">
          {events.map((e) => {
            const upcoming = (e.date || "") >= today;
            return (
              <div key={e.id} data-testid={`cal-${e.id}`} className="flex items-center justify-between gap-3 bg-white/60 border border-[#EAE0D2] rounded-xl px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-[#F0E7D8] flex flex-col items-center justify-center text-[#8a6d2f] shrink-0">
                    <CalendarDays size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-[#2C2A28] truncate">{e.title}</div>
                    <div className="text-xs text-[#A99F90]">{e.date} · {e.type}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={upcoming ? "bg-[#8FB996]/15 text-[#5a9a6a] border-[#8FB996]/30" : "bg-[#EAE0D2] text-[#8A7F70] border-0"}>{upcoming ? "Upcoming" : "Past"}</Badge>
                  <button data-testid={`cal-del-${e.id}`} onClick={() => remove(e.id)} className="text-[#b06b6b] hover:bg-[#C99FA0]/10 rounded-lg p-1"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={ui.dialog}>
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Add reminder</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="col-span-2"><TField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="cal-title" /></div>
            <TSelect label="Type" value={form.type} options={options.event_types || []} onChange={(v) => setForm({ ...form, type: v })} testid="cal-type" />
            <TField label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="cal-date" />
            <div className="col-span-2"><TField label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="cal-notes" /></div>
          </div>
          <DialogFooter>
            <OutlineButton onClick={() => setOpen(false)}>Cancel</OutlineButton>
            <GoldButton data-testid="cal-save" onClick={add} disabled={saving}>{saving ? <Loader2 size={15} className="animate-spin" /> : null} Save</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
